import { isNullOrUndefinedOrEmpty } from 'rewire-common';
import { freeze } from 'rewire-core';
import { Modal, Form, IFormContext } from 'rewire-ui';
import { getNameSearcher } from '../utility/Searchers';
import { DeepPartial, IView, IViewOptions, ViewInsertInput, ViewUpdateInput } from '../DataTypes';
import DataMutations from '../graphql/Mutations';
import mainPageModel from '../main-navigation/MainPageModel';
import { notifications } from '../ui/Notifications';
import createRichTextBoxEditor from '../ui/createRichTextBoxEditor';
import { RouteManagerBase } from '../main-navigation/RouteManager';

export interface ISearchViewDialogData {
  id?: string;
  name?: string;
  fields?: Record<string, any>;
  options?: IViewOptions;
}

export interface ISearchViewDialogModelState {
  id?: string;
}

export class SearchViewDialogModel extends Modal<ISearchViewDialogModelState> {
  form: Form;
  fields?: Record<string, any>;
  options?: IViewOptions;

  constructor() {
    super('');

    const viewSearcher = getNameSearcher('views', () => ({ field: 'type', eq: 'view' }), 'id name other');
    this.form = Form.create((form: IFormContext) => ({
      name: form
        .string()
        .label('Name')
        .validators('required')
        .placeholder('enter search view name')
        .disabled(() => this.isUpdating)
        .autoFocus(),
      view: form
        .reference(viewSearcher)
        .label('View')
        .validators('required')
        .placeholder('select search view')
        .onValueChange(this.handleViewChange)
        .disabled(() => !this.isUpdating)
        .autoFocus(),
      generalComments: form
        .custom(createRichTextBoxEditor())
        .label('General Comments')
        .disabled(() => this.isUpdating && !this.form.field.view.value),
      update: form
        .boolean()
        .label('Update Existing View')
        .onValueChange(this.handleUpdateValueChange)
        .disabled(() => this.isEditing || !mainPageModel.canAddViews || !mainPageModel.canEditViews),
    }));
    this.action('cancel').action('save', this.submit, { variant: 'contained', type: 'submit' });
  }

  reset() {
    this.form.reset();
    this.state.id = undefined;
    this.fields = undefined;
    this.options = undefined;
  }

  load(data: ISearchViewDialogData) {
    if (!mainPageModel.canAddViews && !mainPageModel.canEditViews) return;
    this.reset();
    this.state.id = data.id;
    this.fields = data.fields;
    this.options = data.options;
    this.form.value = {
      name: data.name,
      generalComments: data.fields?.['generalComments:t'],
      view: undefined,
      update: !mainPageModel.canAddViews,
    };
    this.initialize();
    this.open();
  }

  get id() {
    return this.state.id;
  }

  get isEditing(): boolean {
    return !!this.id;
  }

  get isUpdating(): boolean {
    return !this.id && this.form.field.update.value;
  }

  get isAdding(): boolean {
    return !this.id && !this.form.field.update.value;
  }

  handleViewChange = (_form: Form, value?: IView) => {
    if (value) {
      this.form.field.generalComments.value = (value as any).other?.fields?.['generalComments:t'];
    }
  };

  handleUpdateValueChange = (_form: Form, _value: any) => {
    this.initialize();
    this.form.field.name.value = undefined;
    this.form.field.view.value = undefined;
    this.form.field.generalComments.value = undefined;
  };

  initialize() {
    freeze(() => {
      this.setTitle();
      this.setFieldVisibility();
    });
  }

  setTitle() {
    this.state.title = `${this.isEditing ? 'Edit' : this.isAdding ? 'Add New' : 'Update'} Search View`;
  }

  setFieldVisibility() {
    this.form.field.name.visible = !this.form.field.name.disabled!(this.form.field.name);
    this.form.field.view.visible = !this.form.field.view.disabled!(this.form.field.view);
    this.form.field.update.visible = !this.form.field.update.disabled!(this.form.field.update);
  }

  private save = async (): Promise<boolean> => {
    let result: DeepPartial<IView>[] | undefined;
    const generalComments = this.form.field.generalComments.value;
    let newFields = this.fields ? { ...this.fields } : undefined;
    if (!isNullOrUndefinedOrEmpty(generalComments)) {
      if (newFields) {
        newFields['generalComments:t'] = generalComments;
      } else {
        newFields = { 'generalComments:t': generalComments };
      }
    }

    if (this.isAdding) {
      const viewInsertInput = {
        name: this.form.field.name.value,
        type: 'view',
        fields: newFields,
        options: this.options,
      };
      result = await DataMutations.insertViews('', [viewInsertInput as ViewInsertInput]);
    } else {
      let viewUpdateInput: ViewUpdateInput | undefined = undefined;
      if (this.isUpdating) {
        viewUpdateInput = {
          id: this.form.field.view.value!.id,
          fields: newFields,
          options: this.options,
        };
      } else if (this.isEditing) {
        viewUpdateInput = {
          id: this.id!,
          fields: newFields,
          name: this.form.field.name.value,
        };
      }
      if (viewUpdateInput) {
        result = await DataMutations.updateViews('', [viewUpdateInput]);
      }
    }

    if (result?.[0]) {
      notifications.success(`Search View ${this.isEditing ? 'Edited' : this.isAdding ? 'Created' : 'Updated'}`);
      await mainPageModel.updateViews();
      if (this.isAdding) {
        RouteManagerBase.current.navigateTo({ viewId: result[0].id });
      }
      return true;
    }

    notifications.error(`Search View ${this.isEditing ? 'Edited' : this.isAdding ? 'Creation' : 'Update'} Failed`);
    return false;
  };

  submit = async () => {
    return await this.form.submitAsync(this.save);
  };
}

const searchViewDialogModel = new SearchViewDialogModel();
export default searchViewDialogModel;
